/**
 * Ugaoo city geolocation helper.
 * Detects the visitor's city via IP (no permission prompt, unlike navigator.geolocation)
 * and caches it for the rest of the browser session so repeat page views are instant.
 *
 * Also self-reveals any element marked `data-pune-only="true" hidden` once the visitor
 * is confirmed to be in Pune — watched continuously via MutationObserver, so it works
 * even when a section's own inline <script> doesn't run (e.g. Shopify CLI hot-reload
 * patches section HTML into the page without executing embedded <script> tags).
 *
 * Usage:
 *   window.UgaooGeo.getCity().then((city) => { ... }); // city is lowercase, e.g. "pune", or "" if unknown
 *   Elements auto-reveal; listen for 'ugaoo:pune-reveal' on the element if you need to react.
 */
(function () {
  const SESSION_KEY = 'ugaoo_city';
  // Primary + fallback IP-geolocation providers. ipwho.is was primary before
  // but started returning 429 Too Many Requests (and was also less accurate —
  // it resolved a Pune connection to "Panvel" in testing). geojs.io is now
  // primary; ipwho.is stays as a fallback in case geojs.io is ever down.
  const API_PROVIDERS = [
    { url: 'https://get.geojs.io/v1/ip/geo.json', getCity: (data) => data && data.city },
    { url: 'https://ipwho.is/', getCity: (data) => data && data.success !== false && data.city },
  ];
  const TIMEOUT_MS = 2000;

  let pending = null;

  function readCache() {
    try {
      const cached = sessionStorage.getItem(SESSION_KEY);
      // A stored "" means an earlier lookup failed/came back empty. Don't
      // trust it — treat it the same as "no cache" so we retry instead of
      // staying stuck on the empty result for the rest of the session.
      if (cached === '') return null;
      return cached === null ? null : cached;
    } catch {
      return null;
    }
  }

  function writeCache(city) {
    try {
      sessionStorage.setItem(SESSION_KEY, city);
    } catch {}
  }

  function fetchFromProvider(provider) {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), TIMEOUT_MS) : null;

    return fetch(provider.url, { credentials: 'omit', signal: controller ? controller.signal : undefined })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('geo lookup failed: HTTP ' + res.status))))
      .then((data) => {
        const city = String(provider.getCity(data) || '').trim().toLowerCase();
        if (!city) return Promise.reject(new Error('geo lookup returned no city'));
        return city;
      })
      .finally(() => {
        if (timer) clearTimeout(timer);
      });
  }

  function fetchCity() {
    // Try each provider in order; only fall through to the next one on
    // failure (network error, timeout, non-2xx, rate limit, empty city).
    return API_PROVIDERS.reduce(
      (chain, provider) => chain.catch(() => fetchFromProvider(provider)),
      Promise.reject()
    ).catch(() => '');
  }

  function getCity() {
    const cached = readCache();
    if (cached !== null) return Promise.resolve(cached);

    if (!pending) {
      pending = fetchCity().then((city) => {
        // Only cache a real result. Caching "" would mean a transient network
        // failure (or a momentary API hiccup) permanently blocks the reveal
        // for the rest of the browser session — every subsequent check would
        // read the empty string back and never retry the lookup.
        if (city !== '') writeCache(city);
        pending = null;
        return city;
      });
    }
    return pending;
  }

  /* ── Auto-reveal watcher ───────────────────────────────────────── */

  let revealInFlight = false;

  function revealPuneElements() {
    const hiddenPuneEls = document.querySelectorAll('[data-pune-only="true"][hidden]');
    if (!hiddenPuneEls.length || revealInFlight) return;
    revealInFlight = true;
    getCity()
      .then((city) => {
        if (city !== 'pune') return;
        hiddenPuneEls.forEach((el) => {
          el.removeAttribute('hidden');
          el.dispatchEvent(new CustomEvent('ugaoo:pune-reveal', { bubbles: true }));
        });
      })
      .finally(() => {
        revealInFlight = false;
      });
  }

  function startWatching() {
    // Nothing on the page is Pune-restricted — don't call the geo API or
    // watch the DOM at all. Only pages/sections with the checkbox turned
    // on for at least one block pay this cost.
    if (!document.querySelector('[data-pune-only="true"]')) return;

    revealPuneElements();
    const target = document.body || document.documentElement;
    const observer = new MutationObserver(() => revealPuneElements());
    observer.observe(target, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startWatching);
  } else {
    startWatching();
  }

  window.UgaooGeo = { getCity };
})();
